import { useEffect, useMemo, useState } from 'react';
import { Button, Field, Modal } from '../ui';
import { quizApi, apiErrorMessage } from '../../services/api';
import { useOrg } from '../../context/OrgContext';
import { useToast } from '../../context/ToastContext';
import {
  quizActions,
  quizEditForm,
  attemptOptions,
  buildQuizPatch,
  validateQuizEdit,
  totalAttempts,
  REASONS,
} from '../../utils/quizActions';
import { QUIZ_STATE } from '../../utils/quizState';

// Edits a launched test. Fields that can no longer change stay visible but
// locked, with the reason, so the owner is not left wondering where they went.
//
// `attemptCount` comes from the results page. From the test list it is not
// known, so the dialog counts attempts itself before unlocking the time limit
// and attempts; until then those stay locked, as the server would refuse them.
export default function EditQuizDialog({ quiz, attemptCount = null, onClose, onSaved }) {
  const org = useOrg();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [counted, setCounted] = useState(attemptCount);
  const [countFailed, setCountFailed] = useState(false);
  const initial = useMemo(() => quizEditForm(quiz), [quiz]);
  const [form, setForm] = useState(initial);

  const actions = quizActions(quiz, { attemptCount: counted });
  const rules = actions.edit;
  const needsCount = actions.started === null;

  useEffect(() => {
    if (!needsCount) return undefined;
    let cancelled = false;
    quizApi.results(org.id, quiz.id)
      .then((res) => {
        if (cancelled) return;
        const count = totalAttempts(res.data?.students);
        if (count == null) setCountFailed(true);
        else setCounted(count);
      })
      .catch(() => { if (!cancelled) setCountFailed(true); });
    return () => { cancelled = true; };
  }, [needsCount, org.id, quiz.id]);

  const lockedHint = (rule) => {
    if (rule.allowed) return null;
    if (rule.reason === REASONS.checking && countFailed) {
      return 'Could not check whether anyone has started this test. Open its results page to change this.';
    }
    return rule.reason;
  };

  const error = validateQuizEdit(initial, form);
  const patch = buildQuizPatch(initial, form);
  const changed = Object.keys(patch).length > 0;
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await quizApi.update(org.id, quiz.id, patch);
      toast.success(`${res.data?.name || form.name.trim()} updated.`);
      onSaved(res.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to update test'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Edit test" onClose={onClose}>
      <div className="form">
        <Field label="Test name">
          <input value={form.name} onChange={set('name')} />
        </Field>
        <Field
          label="Opens (your local time)"
          hint={rules.opensAt.allowed ? 'Leave empty to open it now.' : lockedHint(rules.opensAt)}
        >
          <input type="datetime-local" value={form.opensAt} disabled={!rules.opensAt.allowed} onChange={set('opensAt')} />
        </Field>
        <Field
          label="Closes (your local time)"
          hint={actions.state === QUIZ_STATE.CLOSED
            ? 'Leave empty for no close time. A later time reopens the test.'
            : 'Leave empty for no close time.'}
        >
          <input type="datetime-local" value={form.closesAt} onChange={set('closesAt')} />
        </Field>
        <Field label="Time limit in minutes" hint={lockedHint(rules.durationMinutes)}>
          <input
            type="number"
            min="1"
            max="600"
            value={form.durationMinutes}
            disabled={!rules.durationMinutes.allowed}
            onChange={set('durationMinutes')}
          />
        </Field>
        <Field label="Allowed attempts" hint={lockedHint(rules.allowedAttempts)}>
          <select value={form.allowedAttempts} disabled={!rules.allowedAttempts.allowed} onChange={set('allowedAttempts')}>
            {attemptOptions(initial.allowedAttempts).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>
        {changed && error && <p className="field-error-text">{error}</p>}
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!changed || Boolean(error) || saving} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
